/**
 * Offers API service
 * Handles communication with the backend for offers
 */

import { apiGet, apiPost, apiPut } from './api';

export interface OfferResponse {
	id: number;
	plan_id: number;
	gardener_user_id: number;
	price_min?: number;
	price_mid?: number;
	price_max?: number;
	price_final?: number;
	proposal_text?: string;
	proposal_summary?: string;
	status: 'draft' | 'submitted' | 'accepted' | 'rejected' | 'withdrawn' | 'expired';
	vat_rate: number;
	currency: string;
	valid_until?: string;
	estimated_timeline?: string;
	project_start_date?: string; // ISO date string - when gardener can start
	render_image_url?: string;
	render_thumbnail_url?: string;
	concept_image_url?: string;
	version: number;
	parent_offer_id?: number;
	metadata?: any;
	internal_notes?: string;
	public_notes?: string;
	timeline_data?: any; // Structured timeline data
	plan_snapshot?: any; // Offer-specific plan copy (when gardener edited/regenerated during offer)
	selected_gardener_ids?: number[];
	selected_gardener_names?: string[];
	created_at: string;
	updated_at: string;
	submitted_at?: string;
	gardener_name?: string;
	company_name?: string;
	gardener_email?: string;
	plan_title?: string;
	plan_owner_id?: number;
}

export interface CreateOfferPayload {
	gardener_user_id: number;
	price_min?: number;
	price_mid?: number;
	price_max?: number;
	price_final?: number;
	proposal_text?: string;
	proposal_summary?: string;
	status?: 'draft' | 'submitted' | 'accepted' | 'rejected' | 'withdrawn' | 'expired';
	vat_rate?: number;
	currency?: string;
	valid_until?: string;
	estimated_timeline?: string;
	project_start_date?: string; // ISO date string - when gardener can start
	render_image_url?: string;
	render_thumbnail_url?: string;
	concept_image_url?: string;
	version?: number;
	parent_offer_id?: number;
	metadata?: any;
	internal_notes?: string;
	public_notes?: string;
	timeline_data?: any; // Structured timeline data (Gantt tasks)
	plan_snapshot?: any; // Offer-specific plan copy (description, features, pricing, instructions, tools, after image)
	selected_gardeners?: Array<{
		id: number;
		role?: string;
		estimated_hours?: number;
		skills?: string[];
	}>;
}

export interface UpdateOfferPayload extends Partial<CreateOfferPayload> {}
export interface OfferCountByPlan {
	plan_id: number;
	offer_count: number;
}

export interface AdminOfferStatsResponse {
	total: number;
	status: { pending: number; accepted: number; rejected: number };
	avg_mid: number;
	accepted_today: number;
}

/**
 * Aggregate offer counts for the admin dashboard (live from database).
 */
export async function getAdminOfferStats(): Promise<AdminOfferStatsResponse> {
	return apiGet<AdminOfferStatsResponse>('/offers?admin_stats=true');
}

/**
 * Get all offers for a specific plan
 */
export async function getOffersForPlan(planId: number): Promise<OfferResponse[]> {
	return apiGet<OfferResponse[]>(`/plans/${planId}/offers`);
}

/**
 * Get offer counts for multiple plan IDs in one request.
 */
export async function getOfferCountsForPlans(planIds: number[]): Promise<OfferCountByPlan[]> {
	const validIds = Array.from(new Set(planIds.filter((id) => Number.isInteger(id) && id > 0)));
	if (validIds.length === 0) return [];
	return apiGet<OfferCountByPlan[]>(`/offers?plan_ids=${validIds.join(',')}`);
}

/**
 * Get all offers for plans owned by a seeker (for My Plans / Offers tab).
 * Returns flat list; frontend groups by plan_id.
 */
export async function getOffersForSeeker(seekerUserId: number): Promise<OfferResponse[]> {
	return apiGet<OfferResponse[]>(`/offers?seeker_id=${seekerUserId}`);
}

/**
 * Get all offers sent by a gardener (for Sent Offers tab).
 */
export async function getOffersByGardener(gardenerUserId: number): Promise<OfferResponse[]> {
	return apiGet<OfferResponse[]>(`/offers?gardener_id=${gardenerUserId}`);
}

/**
 * Get a specific offer by ID
 */
export async function getOfferById(
	offerId: number,
	access?: { gardenerId?: number; seekerId?: number }
): Promise<OfferResponse> {
	const params = new URLSearchParams();
	if (access?.gardenerId) params.set('gardener_id', String(access.gardenerId));
	if (access?.seekerId) params.set('seeker_id', String(access.seekerId));
	const query = params.toString();
	return apiGet<OfferResponse>(`/offers/${offerId}${query ? `?${query}` : ''}`);
}

/**
 * Create a new offer for a plan
 */
export async function createOffer(planId: number, payload: CreateOfferPayload): Promise<OfferResponse> {
	return apiPost<OfferResponse>(`/plans/${planId}/offers`, payload);
}

/**
 * Update an offer
 */
export async function updateOffer(offerId: number, payload: UpdateOfferPayload): Promise<OfferResponse> {
	return apiPut<OfferResponse>(`/offers/${offerId}`, payload);
}

/**
 * Accept an offer
 */
export async function acceptOffer(offerId: number): Promise<OfferResponse> {
	return apiPost<OfferResponse>(`/offers/${offerId}/accept`, {});
}

/**
 * Get project id for an accepted offer (for notification deep links).
 */
export async function getProjectByOfferId(offerId: number): Promise<{ project_id: number }> {
	return apiGet<{ project_id: number }>(`/offers/${offerId}/project`);
}

/**
 * Ensure a project exists for an accepted offer (creates one if missing). Idempotent.
 */
export async function ensureProjectForOffer(offerId: number): Promise<{ project_id: number; created: boolean }> {
	return apiPost<{ project_id: number; created: boolean }>(`/offers/${offerId}/ensure-project`, {});
}

/**
 * Reject an offer
 */
export async function rejectOffer(offerId: number): Promise<OfferResponse> {
	return apiPost<OfferResponse>(`/offers/${offerId}/reject`, {});
}

/**
 * Auto-assign gardeners to an offer's timeline based on skills and availability.
 * Uses backend logic to populate gardenerIds per timeline task.
 */
export async function autoAssignOfferGardeners(offerId: number): Promise<OfferResponse> {
	return apiPost<OfferResponse>(`/offers/${offerId}/auto-assign-gardeners`, {});
}

